import {useId, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {PI_CARDINAL} from '../pi/piPaths';

/** What `keyframes` returns; styled-components does not export the type. */
type Keyframes = ReturnType<typeof keyframes>;

/**
 * Shared look for the cute widgets: a felt palette, per-instance SVG filter
 * ids, the filter definitions themselves, the felt-board card and controls
 * the widgets sit in, and the "poke" animations that play when a widget's
 * pieces are clicked.
 */

/** Felt palette shared by every widget. */
export const FELT = {
  cream: '#F6E9D3',
  creamLight: '#FFF9EE',
  creamDark: '#E6D0AE',
  cardinal: PI_CARDINAL,
  ink: '#2A1E1C',
  board: '#FBF4E8',
  blue: '#4F86C6',
  gold: '#E0A030',
  mint: '#4FB58A',
  pink: '#F29BB3',
} as const;

/** Candy colors for playful sets of items (balls, tokens). */
export const CANDY = [
  FELT.cardinal,
  FELT.gold,
  FELT.blue,
  FELT.mint,
  FELT.pink,
] as const;

/** SVG filter ids for one widget instance. */
export interface FeltIds {
  /** Roughens edges into felt. */
  fuzz: string;
  /** Soft blur, for highlights and shadows. */
  soft: string;
}

/**
 * Returns filter ids unique to the calling component instance, so several
 * widgets can share a page without their `url(#…)` references colliding.
 *
 * @return Ids to pass to {@link FeltDefs} and reference with `url(#id)`.
 * @throws Never.
 */
export function useFeltIds(): FeltIds {
  // useId output contains colons, which break `url(#...)` references.
  const reactId = useId();
  const base = `felt${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;
  return {fuzz: `${base}-fuzz`, soft: `${base}-soft`};
}

/**
 * The felt filter definitions; render inside an SVG's `<defs>`.
 *
 * @param props.ids - Ids from {@link useFeltIds}.
 * @return The filter elements.
 * @throws Never.
 */
export function FeltDefs({ids}: {ids: FeltIds}) {
  return (
    <>
      <filter
        id={ids.fuzz}
        filterUnits="userSpaceOnUse"
        x={-100}
        y={-100}
        width={2000}
        height={2000}
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.9"
          numOctaves={2}
          seed={5}
        />
        <feDisplacementMap in="SourceGraphic" scale={2.5} />
      </filter>
      <filter id={ids.soft}>
        <feGaussianBlur stdDeviation={1.5} />
      </filter>
    </>
  );
}

/** A widget's card: cream felt with a stitched cardinal border. */
export const FeltBoard = styled.section`
  background: ${FELT.board};
  color: ${FELT.ink};
  border-radius: 24px;
  padding: 1.75rem;
  outline: 2px dashed rgba(140, 21, 21, 0.45);
  outline-offset: -10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
`;

/** A widget's title. */
export const WidgetTitle = styled.h2`
  font-family: 'Jost', system-ui, sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
`;

/** One line under a widget's title naming the concept it shows. */
export const WidgetConcept = styled.p`
  margin: 0.25rem 0 1rem;
  color: rgba(42, 30, 28, 0.7);
  font-size: 0.95rem;
`;

/** A row of controls under a widget. */
export const ControlRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 1rem;
`;

/** A soft, pill-shaped felt button. */
export const FeltButton = styled.button<{$isOn?: boolean}>`
  font: inherit;
  font-size: 0.95rem;
  padding: 0.45rem 1.05rem;
  border-radius: 999px;
  cursor: pointer;
  color: ${({$isOn}) => ($isOn ? '#fff' : FELT.ink)};
  background: ${({$isOn}) => ($isOn ? FELT.cardinal : FELT.cream)};
  border: 2px dashed
    ${({$isOn}) => ($isOn ? 'rgba(255,255,255,0.6)' : 'rgba(140,21,21,0.35)')};
  box-shadow: 0 2px 0 ${FELT.creamDark};

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }
`;

/** A labeled range slider row. */
export const SliderLabel = styled.label`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.2rem 0.75rem;
  font-size: 0.9rem;
  width: 100%;

  input {
    grid-column: 1 / -1;
    accent-color: ${FELT.cardinal};
  }
`;

/** A small, muted readout line. */
export const Readout = styled.p`
  margin: 0.75rem 0 0;
  text-align: center;
  font-size: 0.95rem;
  color: rgba(42, 30, 28, 0.8);
`;

/** The poke animations a clicked piece can play. */
export type PokeAnimation = 'boing' | 'jelly' | 'twirl' | 'puff';

const POKE_FRAMES: Record<PokeAnimation, string> = {
  boing: `
    0% { transform: translateY(0) scale(1, 1); }
    25% { transform: translateY(0) scale(1.22, 0.78); }
    50% { transform: translateY(-7px) scale(0.86, 1.18); }
    75% { transform: translateY(0) scale(1.07, 0.94); }
    100% { transform: translateY(0) scale(1, 1); }
  `,
  jelly: `
    0% { transform: scale(1, 1); }
    20% { transform: scale(1.12, 0.82); }
    40% { transform: scale(0.9, 1.14); }
    60% { transform: scale(1.06, 0.94); }
    80% { transform: scale(0.98, 1.03); }
    100% { transform: scale(1, 1); }
  `,
  twirl: `
    0% { transform: rotate(0) scale(1); }
    50% { transform: rotate(200deg) scale(1.25); }
    100% { transform: rotate(360deg) scale(1); }
  `,
  puff: `
    0% { transform: scale(1); }
    35% { transform: scale(1.07); }
    65% { transform: scale(0.97); }
    100% { transform: scale(1); }
  `,
};

/**
 * Two identical copies of each animation under different names. Swapping
 * between them on every poke restarts the animation without remounting the
 * element (which would drop keyboard focus from a focused button).
 */
const POKE_KEYFRAMES: Record<PokeAnimation, readonly [Keyframes, Keyframes]> = {
  boing: [keyframes`${POKE_FRAMES.boing}`, keyframes`${POKE_FRAMES.boing}`],
  jelly: [keyframes`${POKE_FRAMES.jelly}`, keyframes`${POKE_FRAMES.jelly}`],
  twirl: [keyframes`${POKE_FRAMES.twirl}`, keyframes`${POKE_FRAMES.twirl}`],
  puff: [keyframes`${POKE_FRAMES.puff}`, keyframes`${POKE_FRAMES.puff}`],
};

/**
 * CSS that plays `animation` once for poke number `nonce`, or nothing when
 * `nonce` is undefined (never poked).
 *
 * @param params.animation - Which animation to play.
 * @param params.nonce - The poke count from {@link usePoke}; undefined for none.
 * @param params.durationMs - Length of the animation. Omitted defaults to 550ms.
 * @return A styled-components CSS fragment.
 * @throws Never.
 */
export function pokeCss({
  animation,
  nonce,
  durationMs = 550,
}: {
  animation: PokeAnimation;
  nonce: number | undefined;
  durationMs?: number;
}) {
  if (nonce === undefined) {
    return css``;
  }
  const frames = POKE_KEYFRAMES[animation][nonce % 2];
  return css`
    animation: ${frames} ${durationMs}ms ease-out;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `;
}

/**
 * An SVG group that plays a poke animation about its own center (or
 * `$origin`) whenever `$nonce` changes. Pair with {@link usePoke}.
 */
export const PokeGroup = styled.g<{
  $animation: PokeAnimation;
  $nonce: number | undefined;
  $origin?: string;
}>`
  cursor: pointer;
  transform-box: fill-box;
  transform-origin: ${({$origin}) => $origin ?? 'center'};
  ${({$animation, $nonce}) => pokeCss({animation: $animation, nonce: $nonce})}
`;

/** Tracks which piece of a widget was poked last. */
export interface Poke {
  /** Records a poke on the piece with id `id`. */
  poke: (id: string) => void;
  /** The running poke count if `id` was the last piece poked, else undefined. */
  nonceFor: (id: string) => number | undefined;
  /** The id of the last piece poked; undefined before any poke. */
  lastId: string | undefined;
}

/**
 * Tracks the most recently poked piece of a widget so its animation can
 * play, and replay on every further poke.
 *
 * @return See {@link Poke}.
 * @throws Never.
 */
export function usePoke(): Poke {
  const [state, setState] = useState<{id: string; nonce: number}>();
  return {
    poke: id => {
      setState(previous => ({id, nonce: (previous?.nonce ?? 0) + 1}));
    },
    nonceFor: id => (state?.id === id ? state.nonce : undefined),
    lastId: state?.id,
  };
}

/**
 * A small dark speech tag pointing down at `(x, y)`, for showing a poked
 * piece's value.
 *
 * @param props.x - Tip x, in the parent SVG's units.
 * @param props.y - Tip y, in the parent SVG's units.
 * @param props.text - Tag text; keep it short.
 * @return The tag group.
 * @throws Never.
 */
export function FeltTag({x, y, text}: {x: number; y: number; text: string}) {
  const width = 12 + text.length * 6.6;
  return (
    <g pointerEvents="none">
      <rect
        x={x - width / 2}
        y={y - 22}
        width={width}
        height={18}
        rx={9}
        fill={FELT.ink}
        opacity={0.88}
      />
      <path
        d={`M ${x - 4} ${y - 4.5} L ${x} ${y} L ${x + 4} ${y - 4.5} Z`}
        fill={FELT.ink}
        opacity={0.88}
      />
      <text
        x={x}
        y={y - 9}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill="#fff"
      >
        {text}
      </text>
    </g>
  );
}
